/**
 * Live Chat API Service
 * Handles all HTTP communication with live chat backend endpoints
 * Consistent with Phase 2 backend API implementation
 */

import axios from 'axios';
import { ILiveChatConversation, ILiveChatMessage, IImageAttachment } from '../../types/liveChat';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const CHAT_BASE = `${API_BASE}/api/v1/chat`;

/**
 * Get JWT token from localStorage
 */
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : ''
  };
};

/**
 * Conversation Management
 */
export const liveChatAPI = {
  /**
   * Create new chat conversation
   */
  createConversation: async (subject?: string, message?: string) => {
    const response = await axios.post<{ success: boolean; data: ILiveChatConversation }>(
      `${CHAT_BASE}/conversations`,
      { subject, message },
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Get user's current/active conversation
   * Returns the most recent conversation (open or otherwise)
   * Allows users to continue previous conversations across sessions
   */
  getActiveConversation: async () => {
    try {
      const response = await axios.get<{ 
        success: boolean; 
        data: { conversations: ILiveChatConversation[]; total: number } 
      }>(
        `${CHAT_BASE}/conversations?limit=1`,
        { headers: getAuthHeader() }
      );
      // Return the most recent conversation if available
      const conversations = response.data.data?.conversations;
      if (conversations && conversations.length > 0) {
        const mostRecent = conversations[0];
        // Only reuse if it's still open/assigned (not resolved/closed)
        if (['open', 'assigned', 'in_progress'].includes(mostRecent.status)) {
          return mostRecent;
        }
      }
      return null;
    } catch (error) {
      // Error means no active conversation
      return null;
    }
  },

  /**
   * Get specific conversation by ID
   */
  getConversation: async (conversationId: string) => {
    const response = await axios.get<{ success: boolean; data: ILiveChatConversation }>(
      `${CHAT_BASE}/conversations/${conversationId}`,
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Get user's conversation history (limited to recent)
   */
  getUserConversations: async (limit: number = 10) => {
    const response = await axios.get<{ 
      success: boolean; 
      data: { conversations: ILiveChatConversation[]; total: number } 
    }>(
      `${CHAT_BASE}/conversations?limit=${limit}`,
      { headers: getAuthHeader() }
    );
    return response.data.data.conversations;
  },

  /**
   * Update conversation status
   */
  updateConversationStatus: async (conversationId: string, status: string) => {
    const response = await axios.put<{ success: boolean; data: ILiveChatConversation }>(
      `${CHAT_BASE}/conversations/${conversationId}`,
      { status },
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Close conversation
   */
  closeConversation: async (conversationId: string) => {
    const response = await axios.post<{ success: boolean; data: ILiveChatConversation }>(
      `${CHAT_BASE}/conversations/${conversationId}/close`,
      {},
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Rate conversation (satisfaction score)
   */
  rateConversation: async (conversationId: string, score: number, feedback?: string) => {
    const response = await axios.post<{ success: boolean; data: ILiveChatConversation }>(
      `${CHAT_BASE}/conversations/${conversationId}/rate`,
      { score, feedback },
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Message Management
   */

  /**
   * Get conversation message history (paginated)
   */
  getMessages: async (conversationId: string, page: number = 1, limit: number = 50) => {
    const response = await axios.get<{
      success: boolean;
      data: {
        messages: ILiveChatMessage[];
        total: number;
        page: number;
      };
    }>(
      `${CHAT_BASE}/conversations/${conversationId}/messages?page=${page}&limit=${limit}`,
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Send message (text or image)
   * Image sent as base64 data URL
   */
  sendMessage: async (
    conversationId: string,
    content: string,
    type: 'text' | 'image' = 'text',
    imageAttachment?: IImageAttachment
  ) => {
    const payload = {
      content,
      type,
      ...(imageAttachment && {
        imageAttachment: {
          base64Data: imageAttachment.base64Data,
          mimeType: imageAttachment.mimeType,
          filename: imageAttachment.filename,
          size: imageAttachment.size,
          width: imageAttachment.width,
          height: imageAttachment.height
        }
      })
    };

    const response = await axios.post<{ success: boolean; data: ILiveChatMessage }>(
      `${CHAT_BASE}/conversations/${conversationId}/messages`,
      payload,
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Edit message
   */
  editMessage: async (messageId: string, newContent: string) => {
    const response = await axios.put<{ success: boolean; data: ILiveChatMessage }>(
      `${CHAT_BASE}/messages/${messageId}`,
      { content: newContent },
      { headers: getAuthHeader() }
    );
    return response.data.data;
  },

  /**
   * Delete message
   */
  deleteMessage: async (messageId: string) => {
    await axios.delete(
      `${CHAT_BASE}/messages/${messageId}`,
      { headers: getAuthHeader() }
    );
  },

  /**
   * Mark messages as read
   */
  markMessagesAsRead: async (conversationId: string, messageIds: string[]) => {
    await axios.post(
      `${CHAT_BASE}/messages/read`,
      { conversationId, messageIds },
      { headers: getAuthHeader() }
    );
  },

  /**
   * Image Upload
   */

  /**
   * Upload image and get base64 data
   * Returns base64-encoded image with metadata
   */
  uploadImage: async (file: File): Promise<IImageAttachment> => {
    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image must be less than 5MB');
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files are supported');
    }

    // Use FileReader for direct base64 conversion (client-side)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const base64Data = event.target?.result as string;

        // Get image dimensions
        const img = new Image();
        img.onload = () => {
          resolve({
            base64Data,
            mimeType: file.type,
            filename: file.name,
            size: file.size,
            width: img.width,
            height: img.height,
            uploadedAt: new Date()
          });
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = base64Data;
      };

      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };

      reader.readAsDataURL(file);
    });
  }
};